import React, { useCallback, useEffect, useState } from 'react';
import {
  App,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { EyeOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { createLotteryCampaign, getLotteryCampaigns } from '../../api/lottery';
import type { LotteryCampaign, LotteryCampaignStats } from '../../types/lottery';
import './index.css';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

interface CampaignFormValues {
  name: string;
  description?: string;
  isActive?: boolean;
  isPrimary?: boolean;
  isStandalone?: boolean;
  dateRange?: [Dayjs, Dayjs];
  rules?: string;
}

const emptyStats: LotteryCampaignStats = {
  drawCount: 0,
  winningCount: 0,
  pendingFulfillmentCount: 0,
  prizeCount: 0,
  activePrizeCount: 0,
  soldOutPrizeCount: 0,
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  return dayjs(value).format('YYYY-MM-DD HH:mm');
};

const formatCampaignTime = (record: LotteryCampaign) => {
  if (!record.startAt && !record.endAt) return '长期有效';
  return `${formatDateTime(record.startAt)} 至 ${formatDateTime(record.endAt)}`;
};

const toCampaignPayload = (values: CampaignFormValues) => ({
  name: values.name,
  description: values.description || null,
  isActive: values.isActive ?? true,
  isPrimary: values.isPrimary ?? false,
  isStandalone: values.isStandalone ?? false,
  startAt: values.dateRange?.[0]?.toISOString() || null,
  endAt: values.dateRange?.[1]?.toISOString() || null,
  rules: values.rules || null,
});

const getStats = (campaign: LotteryCampaign) => campaign.stats || emptyStats;

const LotteryPage: React.FC = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<LotteryCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [campaignModalVisible, setCampaignModalVisible] = useState(false);
  const [createCampaignForm] = Form.useForm<CampaignFormValues>();

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getLotteryCampaigns();
      setCampaigns(data || []);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '获取抽奖活动失败');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const openCampaignModal = () => {
    createCampaignForm.setFieldsValue({
      name: '',
      description: '',
      isActive: true,
      isPrimary: false,
      isStandalone: false,
      rules: '仅成功订单可参与，每个订单限抽一次。中奖后由客服人工联系兑奖。',
    });
    setCampaignModalVisible(true);
  };

  const handleCreateCampaign = async () => {
    try {
      const values = await createCampaignForm.validateFields();
      const nextCampaign = await createLotteryCampaign(toCampaignPayload(values));
      message.success('活动已创建');
      setCampaignModalVisible(false);
      navigate(`/lottery/${nextCampaign.id}`);
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || '创建活动失败');
    }
  };

  const columns: ColumnsType<LotteryCampaign> = [
    {
      title: '活动',
      dataIndex: 'name',
      render: (name, record) => (
        <Space direction="vertical" size={4}>
          <Space wrap>
            <Text strong>{name}</Text>
            {record.isPrimary && <Tag color="blue">客户端主活动</Tag>}
            <Tag color={record.isActive ? 'green' : 'red'}>{record.isActive ? '启用' : '停用'}</Tag>
            {record.isStandalone ? <Tag color="purple">独立资格</Tag> : <Tag>共享资格</Tag>}
          </Space>
          {record.description && <Text type="secondary">{record.description}</Text>}
        </Space>
      ),
    },
    {
      title: '活动时间',
      width: 260,
      render: (_, record) => formatCampaignTime(record),
    },
    {
      title: '数据概览',
      width: 320,
      render: (_, record) => {
        const stats = getStats(record);
        return (
          <Space size={12} wrap className="lottery-metric-line">
            <span>抽奖 {stats.drawCount}</span>
            <span>中奖 {stats.winningCount}</span>
            <span>待兑奖 {stats.pendingFulfillmentCount}</span>
            <span>奖品 {stats.activePrizeCount}/{stats.prizeCount}</span>
          </Space>
        );
      },
    },
    {
      title: '库存',
      width: 120,
      render: (_, record) => {
        const stats = getStats(record);
        return stats.soldOutPrizeCount > 0 ? (
          <Tag color="red">{stats.soldOutPrizeCount} 个售罄</Tag>
        ) : (
          <Tag color="green">正常</Tag>
        );
      },
    },
    {
      title: '操作',
      width: 120,
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/lottery/${record.id}`)}
        >
          管理
        </Button>
      ),
    },
  ];

  return (
    <div className="lottery-page">
      <div className="lottery-header">
        <div>
          <Title level={4}>抽奖活动</Title>
          <Text type="secondary">每个活动独立配置奖品和记录，客户端默认展示当前可用的主活动。</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchCampaigns}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCampaignModal}>
            新建活动
          </Button>
        </Space>
      </div>

      <div className="lottery-panel">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={campaigns}
          loading={loading}
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: false }}
        />
      </div>

      <Modal
        title="新建活动"
        open={campaignModalVisible}
        onCancel={() => setCampaignModalVisible(false)}
        onOk={handleCreateCampaign}
        okText="创建并配置"
        destroyOnClose
      >
        <Form form={createCampaignForm} layout="vertical" preserve={false}>
          <Form.Item name="name" label="活动名称" rules={[{ required: true, message: '请输入活动名称' }]}>
            <Input placeholder="例如：暑期办卡抽奖" />
          </Form.Item>
          <Form.Item name="description" label="活动说明">
            <Input placeholder="展示给用户看的简短说明" />
          </Form.Item>
          <Form.Item name="isActive" label="活动开关" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
          <Form.Item name="isPrimary" label="客户端主活动" valuePropName="checked">
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
          <Form.Item
            name="isStandalone"
            label="独立抽奖资格"
            valuePropName="checked"
            extra="默认关闭。关闭时共享历史抽奖状态；开启时本活动独立计算。"
          >
            <Switch checkedChildren="独立" unCheckedChildren="共享" />
          </Form.Item>
          <Form.Item name="dateRange" label="活动时间">
            <RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="rules" label="活动规则">
            <TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LotteryPage;
